import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type { Point as GeoJsonPoint, Geometry } from 'geojson';
import type { Coordinate } from 'ol/coordinate';
import type Point from 'ol/geom/Point';

import { backendSingleExecution } from '@disy/lib.backend/single-execution';
import { getCadenzaTopWindow } from '@disy/lib/iframe';
import { logger } from '@disy/lib/logging';
import { cadenzaSessionStorage } from '@disy/lib/storage';
import { getSetting } from '@disy/workbook.lib/settings';

import type { DimensionDto } from 'cadenza/api-client/dto/dimension-dto';
import type { WorldBoxDto } from 'cadenza/api-client/dto/world-box-dto';
import { findAddresses } from 'cadenza/api-client/geocoding-api';
import type { Authority } from 'cadenza/api-client/srs-api';
import { find3Words } from 'cadenza/api-client/word3words-api';
import { getObjectInfo } from 'cadenza/api-client/workbook-api';
import type { GetExternalLayerObjectInfo } from 'cadenza/api-client/workbook-map-api';
import { getExternalLayerObjectInfo } from 'cadenza/api-client/workbook-map-api';
import type { ReverseGeocodedLocationDto } from 'cadenza/geocoding/dto/reverse-geocoded-location-dto';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import {
  type RootState,
  type RootStateOr,
  createAsyncThunk,
  createSliceFunction,
  getStore,
  hasSecondaryStore,
} from 'cadenza/store/cadenza-store';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key.gen';
import type { ObjectInfoElementsDto } from 'cadenza/workbook/model/object-info-elements-dto';
import type { SelectionRecordsDto } from 'cadenza/workbook/selection/model/selection-records-dto';
import { createObjectInfoElements } from 'cadenza/workbook/selection/selection-utils';
import {
  getSelection,
  getSelectionExternalLayer,
  getSelectionInOtherLayers,
  getViewByDataViewId,
  getViewByLayerId,
} from 'cadenza/workbook/workbook-core-selectors.state';

export const SLICE_PREFIX = 'selectionCart';
const SELECTION_CART_KEY = 'selection-cart';

declare module 'cadenza/store/cadenza-store' {
  interface RootState {
    [SLICE_PREFIX]: SelectionCartState;
  }
}

const slice = createSliceFunction(SLICE_PREFIX);
const cadenzaStorage = cadenzaSessionStorage;

interface SelectionCartState extends SelectionCartStorageState {
  // if selection cart state should be stored in cadenza session storage on each change (only the relevant selection cart state properties)
  storageEnabled: boolean;
  dirty: boolean;
  selectionClickPx?: [number, number];
  objectInfo?: ObjectInfo;
  // These also holds the address/what3words for click-coordinate at 0 index
  addressByObjectInfoIndex: Record<number, AddressInfo | undefined>;
  what3wordsByObjectInfoIndex: Record<number, What3Words | undefined>;
  objectInfoLoadingState: SectionLoadingState;
  otherLayersLoadingState: SectionLoadingState;
  additionalMapClientSideHits: SelectionRecordsDto[];
}

interface SelectionCartStorageState {
  lastMaximizeSize?: [number, number];
  lastPosition?: Position;
  manuallyResized: boolean;
  manuallyPositioned: boolean;
  minimized: boolean;
  lastMapSelectionRequest?: MapSelectionRequest;
  objectInfoExpanded: Record<LayerId, boolean>;
}

interface ObjectInfo {
  layerId: LayerId;
  elements: ObjectInfoElementsDto[];
  currentIndex: number;
}

interface AddressInfo {
  loadingState: SectionLoadingState;
  address?: Address;
}

export interface Address {
  formattedAddressLines: string[];
  addressSearchStartPoint: GeoJsonPoint;
  addressSearchEndPoint: GeoJsonPoint;
}

interface What3Words {
  loadingState: SectionLoadingState;
  what3wordsResult?: string;
  // Optional even when there was error because title is not always available
  errorTitle?: string;
}

export interface MapSelectionRequest {
  geometry: Geometry;
  mapSize: DimensionDto;
  mapScale: number;
  mapExtent: WorldBoxDto;
  selectableLayers?: LayerId[];
}

interface Position {
  top: string;
  right: string;
  bottom: string;
  left: string;
  width: string;
  height: string;
}

export type SectionLoadingState = 'DEFAULT' | 'LOADING' | 'LOADING_COMPLETE' | 'ERROR';

export const objectInfoSingleExecution = backendSingleExecution(getObjectInfo);
export const externalLayerObjectInfoSingleExecution = backendSingleExecution(getExternalLayerObjectInfo);

export const findAddress = createAsyncThunk<
  ReverseGeocodedLocationDto,
  // index is used to in reducer to save the address to specific index
  { point: Point; authority: Authority; index: number }
>(SLICE_PREFIX + '/findAddress', async ({ point, authority }, thunkApi) => {
  try {
    const address = await findAddresses(point, authority);
    if (address) {
      return address;
    } else {
      return thunkApi.rejectWithValue(undefined);
    }
  } catch (error) {
    logger.error('Failed to retrieve address', error);
    return thunkApi.rejectWithValue(error);
  }
});

export const findWhat3Words = createAsyncThunk<
  string,
  // index is used to in reducer to save the address to specific index
  { coordinates: Coordinate; srsCode: string; index: number }
>(SLICE_PREFIX + '/findWhat3Words', async ({ coordinates, srsCode }, thunkApi) => {
  try {
    return await find3Words(coordinates, srsCode);
  } catch (error) {
    logger.error('Failed to retrieve what3words result', error);
    return thunkApi.rejectWithValue(error);
  }
});

export const fetchObjectInfo = createAsyncThunk<
  ObjectInfo,
  {
    workbookNameAndHash: WorkbookNameAndHash;
    mapSrs: string;
    mapResolution: number;
    layerId: LayerId;
  }
>(SLICE_PREFIX + '/fetchObjectInfo', ({ workbookNameAndHash, mapSrs, mapResolution, layerId }, thunkApi) => {
  return objectInfoSingleExecution(workbookNameAndHash, mapSrs, mapResolution)
    .then((elements) => {
      return {
        elements,
        layerId,
        currentIndex: 0,
      };
    })
    .catch((error) => thunkApi.rejectWithValue(error));
});

export const fetchExternalLayerObjectInfo = createAsyncThunk<
  ObjectInfo,
  {
    externalLayerObjectInfoRequest: GetExternalLayerObjectInfo;
  }
>(SLICE_PREFIX + '/fetchExternalLayerObjectInfo', ({ externalLayerObjectInfoRequest }, thunkApi) => {
  return externalLayerObjectInfoSingleExecution(externalLayerObjectInfoRequest)
    .then((externalLayerObjectInfo) => {
      return {
        layerId: externalLayerObjectInfoRequest.layerId,
        elements: createObjectInfoElements(externalLayerObjectInfo),
        currentIndex: 0,
      };
    })
    .catch((error) => thunkApi.rejectWithValue(error));
});

function createInitialStateFromStorage() {
  const initialState = createInitialState();
  const storedSelectionCartState = cadenzaStorage.getItem(SELECTION_CART_KEY);
  if (initialState.storageEnabled && storedSelectionCartState) {
    return Object.assign(initialState, JSON.parse(storedSelectionCartState));
  } else {
    return initialState;
  }
}

function createInitialState() {
  const isSecondaryStore = hasSecondaryStore();
  const isInIframe = getCadenzaTopWindow() !== window;
  const initialState: SelectionCartState = {
    // in case that selection cart is opened in iframe(e.g. drill through) or secondary store is initialized (e.g. map fullscreen dialog is open)
    // then do not store selection cart state in cadenza storage
    storageEnabled: !(isSecondaryStore || isInIframe),
    dirty: false,
    manuallyResized: false,
    manuallyPositioned: false,
    minimized: false,
    addressByObjectInfoIndex: {},
    what3wordsByObjectInfoIndex: {},
    objectInfoLoadingState: 'DEFAULT',
    otherLayersLoadingState: 'DEFAULT',
    objectInfoExpanded: {},
    additionalMapClientSideHits: [],
  };
  return initialState;
}

function storeSelectionCartStateInStorage(state: SelectionCartState) {
  if (!state.storageEnabled) {
    return;
  }

  const selectionCartStorageState: SelectionCartStorageState = {
    lastMaximizeSize: state.lastMaximizeSize,
    lastPosition: state.lastPosition,
    manuallyPositioned: state.manuallyPositioned,
    manuallyResized: state.manuallyResized,
    minimized: state.minimized,
    lastMapSelectionRequest: state.lastMapSelectionRequest,
    objectInfoExpanded: state.objectInfoExpanded,
  };

  cadenzaStorage.setItem(SELECTION_CART_KEY, JSON.stringify(selectionCartStorageState));
}

function storeObjectInfoOptions(state: SelectionCartState, objectInfo?: ObjectInfo) {
  state.objectInfo = objectInfo;
  state.objectInfoLoadingState = 'LOADING_COMPLETE';
  state.addressByObjectInfoIndex = {};
  state.what3wordsByObjectInfoIndex = {};
}

function setObjectInfoOptionsOnFetchError(state: SelectionCartState) {
  state.objectInfo = undefined;
  state.objectInfoLoadingState = 'ERROR';
  state.addressByObjectInfoIndex = {};
  state.what3wordsByObjectInfoIndex = {};
}

export const selectionCartSlice = createSlice({
  name: SLICE_PREFIX,
  initialState: createInitialStateFromStorage(),
  reducers: {
    setDirty(state, action: PayloadAction<boolean>) {
      state.dirty = action.payload;
      // When it is unmarked as dirty, clear also selectionClickPx, so that the position is not changed on subsequent re-render if not necessary.
      // eg: when switching selection from results in other layers.
      if (!state.dirty) {
        state.selectionClickPx = undefined;
      }
    },
    setLastPosition(
      state,
      action: PayloadAction<{
        position: Position;
        updateLastMaximizeSize: boolean;
      }>,
    ) {
      state.lastPosition = action.payload.position;
      if (action.payload.updateLastMaximizeSize) {
        state.lastMaximizeSize = [parseInt(state.lastPosition.width), parseInt(state.lastPosition.height)];
      }
      storeSelectionCartStateInStorage(state);
    },

    markManuallyResized(state) {
      state.manuallyResized = true;
      storeSelectionCartStateInStorage(state);
    },

    markManuallyPositioned(state) {
      state.manuallyPositioned = true;
      storeSelectionCartStateInStorage(state);
    },

    setMinimized(state, action: PayloadAction<boolean>) {
      state.minimized = action.payload;
      storeSelectionCartStateInStorage(state);
    },

    setSelectionClickPx(state, action: PayloadAction<[number, number] | undefined>) {
      state.selectionClickPx = action.payload;
    },

    setLastMapSelectionRequest(state, action: PayloadAction<MapSelectionRequest | undefined>) {
      state.lastMapSelectionRequest = action.payload;
      storeSelectionCartStateInStorage(state);
    },

    setAdditionalMapClientSideHits(state, action: PayloadAction<SelectionRecordsDto[] | undefined>) {
      state.additionalMapClientSideHits = action.payload;
    },

    setObjectInfoIndex(state, action: PayloadAction<number>) {
      state.objectInfo!.currentIndex = action.payload;
    },

    setObjectInfoExpanded(state, action: PayloadAction<boolean>) {
      state.objectInfoExpanded[state.objectInfo!.layerId] = action.payload;
    },

    setObjectInfoOptions(state, action: PayloadAction<ObjectInfo | undefined>) {
      storeObjectInfoOptions(state, action.payload);
    },

    setOtherLayersLoadingState(state, action: PayloadAction<SectionLoadingState>) {
      state.otherLayersLoadingState = action.payload;
    },

    clearRelevantStateOnSelectionChange(
      state,
      action: PayloadAction<{
        // needed from outside because the respective selector requires RootState
        minimized: boolean;
        isSelectionAvailable: boolean;
        isMapSelected: boolean;
      }>,
    ) {
      if (action.payload.minimized || !action.payload.isSelectionAvailable) {
        state.objectInfo = undefined;
        state.objectInfoLoadingState = 'DEFAULT';
        state.addressByObjectInfoIndex = {};
        state.what3wordsByObjectInfoIndex = {};
      }
      if (!action.payload.isMapSelected) {
        state.clickCoordinate = undefined;
        storeSelectionCartStateInStorage(state);
      }
    },

    clear(state) {
      // keep minimized state after clear
      const initialState = createInitialState();
      initialState.minimized = state.minimized;
      initialState.objectInfoExpanded = state.objectInfoExpanded;
      storeSelectionCartStateInStorage(initialState);
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchObjectInfo.pending, (state) => {
        state.objectInfoLoadingState = 'LOADING';
      })
      .addCase(fetchObjectInfo.fulfilled, (state, action) => {
        storeObjectInfoOptions(state, action.payload);
      })
      .addCase(fetchObjectInfo.rejected, (state, action) => {
        setObjectInfoOptionsOnFetchError(state);
      })
      .addCase(fetchExternalLayerObjectInfo.pending, (state) => {
        state.objectInfoLoadingState = 'LOADING';
      })
      .addCase(fetchExternalLayerObjectInfo.fulfilled, (state, action) => {
        storeObjectInfoOptions(state, action.payload);
      })
      .addCase(fetchExternalLayerObjectInfo.rejected, (state, action) => {
        setObjectInfoOptionsOnFetchError(state);
      })
      .addCase(findAddress.pending, (state, { meta }) => {
        state.addressByObjectInfoIndex[meta.arg.index] = {
          loadingState: 'LOADING',
        };
      })
      .addCase(findAddress.fulfilled, (state, { payload: { point, formattedAddressLines }, meta }) => {
        // current index cannot be used, because the user can navigate way which changes currentIndex
        // Only do the change, if the previous value was 'LOADING'.
        // This is necessary to prevent race condition when object-info is fetched after finding the address.
        // The fetch of object-info will clear the address, but we will again add the address below which will show the wrong result.
        // This can happen if you find address, and immediately switch the selection from results in other-layers.
        const addressInfo = state.addressByObjectInfoIndex[meta.arg.index];
        if (addressInfo?.loadingState === 'LOADING') {
          addressInfo.loadingState = 'LOADING_COMPLETE';
          addressInfo.address = {
            formattedAddressLines,
            addressSearchStartPoint: {
              type: 'Point',
              coordinates: meta.arg.point.getCoordinates(),
            },
            addressSearchEndPoint: point,
          };
        }
      })
      .addCase(findAddress.rejected, (state, { meta }) => {
        const addressInfo = state.addressByObjectInfoIndex[meta.arg.index];
        if (addressInfo?.loadingState === 'LOADING') {
          addressInfo.loadingState = 'ERROR';
        }
      })
      .addCase(findWhat3Words.pending, (state, { meta }) => {
        state.what3wordsByObjectInfoIndex[meta.arg.index] = {
          loadingState: 'LOADING',
        };
      })
      .addCase(findWhat3Words.fulfilled, (state, { payload, meta }) => {
        // current index cannot be used, because the user can navigate way which changes currentIndex
        const what3Words = state.what3wordsByObjectInfoIndex[meta.arg.index];
        if (what3Words?.loadingState === 'LOADING') {
          what3Words.loadingState = 'LOADING_COMPLETE';
          what3Words.what3wordsResult = payload;
        }
      })
      .addCase(findWhat3Words.rejected, (state, { payload, meta }) => {
        const what3Words = state.what3wordsByObjectInfoIndex[meta.arg.index];
        if (what3Words?.loadingState === 'LOADING') {
          what3Words.loadingState = 'ERROR';
          if (typeof payload === 'object' && payload && 'title' in payload) {
            what3Words.errorTitle = payload['title'] as string;
          }
        }
      });
  },
});

const { reducer, actions } = selectionCartSlice;
getStore().registerReducer(SLICE_PREFIX, reducer);

export const {
  setDirty,
  setSelectionClickPx,
  setLastPosition,
  setLastMapSelectionRequest,
  setAdditionalMapClientSideHits,
  markManuallyResized,
  markManuallyPositioned,
  setMinimized,
  setObjectInfoIndex,
  setObjectInfoExpanded,
  setObjectInfoOptions,
  setOtherLayersLoadingState,
  clearRelevantStateOnSelectionChange,
  clear,
} = actions;

export function getLastPosition(state: RootStateOr<SelectionCartState>) {
  return slice(state).lastPosition;
}

export function getLastMapSelectionRequest(state: RootStateOr<SelectionCartState>) {
  return slice(state).lastMapSelectionRequest;
}

export function getSelectionClickPx(state: RootStateOr<SelectionCartState>) {
  return slice(state).selectionClickPx;
}

export function getLastMaximizeSize(state: RootStateOr<SelectionCartState>) {
  return slice(state).lastMaximizeSize;
}

export function isMinimized(state: RootState) {
  return isSelectionOnMap(state) ? slice(state).minimized : true;
}

export function isSelectionOnMap(state: RootState) {
  const view = getSelectionView(state);
  return view ?
      view.type === 'MAP' && !getSetting(view, MapViewSettingsKey.toPersistenceKey('TIMELINE_3D_ENABLED'), false)
    : false;
}

export function getSelectionView(state: RootState) {
  const selection = getSelection(state);
  const selectionExternalLayer = getSelectionExternalLayer(state);
  if (selection?.records) {
    return getViewByDataViewId(state, selection.records.dataViewId);
  } else if (selectionExternalLayer) {
    return getViewByLayerId(state, selectionExternalLayer.layerId);
  }
}

export const getObjectInfoOptions = createSelector(
  (state: RootState) => slice(state).objectInfo,
  (state: RootState) => slice(state).objectInfoLoadingState,
  (state: RootState) =>
    slice(state).objectInfo?.layerId ? slice(state).objectInfoExpanded[slice(state).objectInfo!.layerId] : false,
  (objectInfo, loadingState, expanded) => ({ objectInfo, loadingState, expanded }),
);

export const getObjectInfoOptionsMinimized = createSelector(
  getObjectInfoOptions,
  (state: RootState) => slice(state).minimized,
  (objectInfoOptions, minimized) => ({ objectInfoOptions, minimized }),
);

export function getCurrentAddress(state: RootState) {
  const objectInfoOptions = getObjectInfoOptions(state);
  return slice(state).addressByObjectInfoIndex[
    objectInfoOptions.objectInfo ? objectInfoOptions.objectInfo.currentIndex : 0
  ];
}

export function getCurrentWhat3Words(state: RootState) {
  const objectInfoOptions = getObjectInfoOptions(state);
  return slice(state).what3wordsByObjectInfoIndex[
    objectInfoOptions.objectInfo ? objectInfoOptions.objectInfo.currentIndex : 0
  ];
}

export function isManuallyResized(state: RootStateOr<SelectionCartState>) {
  return slice(state).manuallyResized;
}

export function isManuallyPositioned(state: RootStateOr<SelectionCartState>) {
  return slice(state).manuallyPositioned;
}

export function isDirty(state: RootStateOr<SelectionCartState>) {
  return slice(state).dirty;
}

export function getSelectionViewId(state: RootState) {
  return getSelectionView(state)?.id;
}

export const getSelectionInOtherLayersInfo = createSelector(
  getSelectionInOtherLayers,
  (state: RootState) => slice(state).otherLayersLoadingState,
  (selectionInOtherLayers, loadingState) => ({ selectionInOtherLayers, loadingState }),
);

export function getAdditionalMapClientSideHits(state: RootStateOr<SelectionCartState>) {
  return slice(state).additionalMapClientSideHits;
}
